/**
 * Netx.Collections.CartAssets reflects the assets in a cart on the DAM -- either a saved cart, or
 * the in-memory cart AKA the "current cart".  It contains a host of RPC methods for manipulating cart
 * contents and for all of the cart-based asset operations on the DAM.
 *
 *     Inherts the paging system from Netx.Collections.Assets to fetch cart assets by page or chunk.
 *
 * @class
 * @todo The new cart API is much nicer, and it'd be nice if this object reflected it.
 *       I'd rather call 'cart.addAssets' than 'cart.cartAdd', etc.  We'd want to create deprecations
 *       around the old methods so they still work for now, but use the new methods in the core controlle
 */
import {userInputToExpirationTime} from '@netx/core-asset-expiration/utils';
import Base from '@netx/core-asset/collections/assets';
import AssetSetModel from '@netx/core-asset/models/assetSet';
import {types as assetSetTypes} from '@netx/core-asset/properties/assetSets';
import {allSavedCarts} from '@netx/core-cart/singletons/savedCarts';
import {i18n} from '@netx/core/i18n';
import customConfig from '../config';
import {unarchiveTypes} from '../properties/cartAssetArchive';

export default class CartAssets extends Base {
	/** @type {boolean} */
	get xhrTrackProgress() {
		return true;
	}

	/** @type {object} */
	get xhrLoadingMessages() {
		return {
			facetedSearch: 'Loading saved cart assets',
			searchAssetBeanObjects: 'Loading saved cart assets',
		};
	}

	/** @type {object} */
	get xhrLoadedMessages() {
		return {
			facetedSearch: 'Loaded saved cart assets',
			searchAssetBeanObjects: 'Loaded saved cart assets',
		};
	}

	/**
	 * @property {object} methods                         - Methods for this Collection
	 * @property {method} methods.addAssetsToCart         - Add assets to cart
	 * @property {method} methods.archiveCartAssets       - Archive all assets in cart
	 * @property {method} methods.createObjectPackage     - Create TMS object package
	 * @property {method} methods.deleteCartAssets        - Delete all assets in cart
	 * @property {method} methods.empty                   - Empty cart
	 * @property {method} methods.expireCartAssets        - Set all assets in cart to expire
	 * @property {method} methods.fetchAll                - Fetch all assets
	 * @property {method} methods.inspect                 - Inspect cart contents
	 * @property {method} methods.removeAssetsFromCart    - Remove assets from cart
	 * @property {method} methods.setAssetsInCart         - Set assets in cart
	 * @property {method} methods.unarchiveCartAssets     - Unarchive all assets in cart
	 */
	get methods() {
		return _.extend(super.methods, {
			/**
			 * Add assets to cart
			 * @method
			 * @param {array} assets Assets/asset ids
			 * @param {object} opts Request options
			 */
			addAssetsToCart(assets, opts) {
				return ['addAssetsToCart', this.id, _.tidyAssetList(assets)];
			},
			/**
			 * Archive cart assets
			 * @method
			 */
			archiveCartAssets: ['archiveCartAssets', 'id', 'archiveArgs.locationId'],
			/**
			 * TMS
			 * @method
			 */
			createObjectPackage: ['createObjectPackage', 'id'],
			/**
			 * Delete all assets in cart
			 * @method
			 */
			deleteCartAssets: ['deleteCartAssets', 'id'],
			/**
			 * Empty cart
			 * @method
			 */
			empty: ['setAssetsInCart', 'id', []],
			/**
			 * Delete all assets in cart
			 * @method
			 */
			expireCartAssets: ['expireCartAssets', 'id', 'expirationArgs.date'],
			/**
			 * Fetch all assets
			 * Usefull for our pull until (chekcing to see if an asset is still present)
			 * @method
			 * @param {object} opts Request options
			 */
			fetchAll(opts) {
				var search = this.selection.createSearchObject();
				return [
					'searchAssetObjects',
					search.sortField,
					search.sortOrder,
					search.matchCriteria,
					search.elementTypes,
					search.elementSubType1s,
					search.elementSubType2s,
					search.elementValue1s,
					search.elementValue2s,
					search.elementValue3s,
					search.saveSearch,
					search.notifyType,
					search.ignoreStat,
					0,
					0,
				];
			},
			/**
			 * Inspects cart assets for repupose
			 * @method
			 */
			inspect: ['inspectCartContents', 'id'],
			/**
			 * Remove assets from cart
			 * @method
			 * @param {array} assets Assets/asset ids
			 * @param {object} opts Request options
			 */
			removeAssetsFromCart(assets, opts) {
				return ['removeAssetsFromCart', this.id, _.tidyAssetList(assets)];
			},
			/**
			 * Set assets in cart
			 * @method
			 * @param {array} assets Assets/asset ids
			 * @param {object} opts Request options
			 */
			setAssetsInCart(assets, opts) {
				return ['setAssetsInCart', this.id, _.tidyAssetList(assets)];
			},
			/**
			 * Unarchive cart assets
			 * @method
			 */
			unarchiveCartAssets: [
				'unarchiveCartAssets',
				'id',
				'unarchiveArgs.type',
				'unarchiveArgs.email',
			],
		});
	}

	/**
	 * @property {object}   parsers                       - Parsers for this collection
	 * @property {function} parsers.addAssetsToCart       - Added assets to cart
	 * @property {function} parsers.addFacetedSearch      - Added assets to cart based on faceted search
	 * @property {function} parsers.addSearch             - Added assets to cart based on search
	 * @property {function} parsers.empty                 - Emptied cart
	 * @property {function} parsers.inspect               - Inspected cart assets
	 * @property {function} parsers.repurpose             - Repurposed assets with preset
	 * @property {function} parsers.removeAssetsFromCart  - Removed assets from cart
	 */
	get parsers() {
		return _.extend(super.parsers, {
			/**
			 * Added assets to cart
			 * @function
			 * @param {number} count Cart asset count
			 * @param {array} assets Assets/asset ids
			 * @param {object} opts Request options
			 */
			addAssetsToCart(count, assets, opts) {
				assets = _.tidyAssetList(assets);
				// TODO: it'd be nice to get the calling arguments included in the complete: event & not have to do this:
				this.listenToOnce(this, 'complete:addAssetsToCart', () => {
					dispatcher.trigger('cart:added', assets);
					this.trigger('added', assets);
				});

				this.cartResultParser(count);
			},
			// /**
			//  * Added assets to cart based on facetedsearch
			//  * @function
			//  * @param {number} count Cart asset count
			//  * @param {module:search/collections/searchRules} selection Selection
			//  * @param {object} opts Request options
			//  */
			// addFacetedSearchToCart(count, selection, opts) {
			// 	this.cartResultParser(count);
			// },
			/**
			 * Emptied cart
			 * @function
			 * @param {number} count Cart asset count
			 * @param {object} opts Request options
			 */
			empty(count, opts) {
				this.reset(); // clear out the collection
				this.pager.clearPages();
				this.trigger('emptied');
				dispatcher.trigger('cart:cleared', this);
			},
			/**
			 * Inspected cart assets
			 * @function
			 * @param {object} data Inspection data
			 * @param {object} opts Request options
			 * @example
			 * {
			 *     "imageCount":2,
			 *     "videoCount":0,
			 *     "audioCount":0,
			 *     "documentCount":0,
			 *     "otherAssetCount":0,
			 *     "downloadApprovalRequired":false,
			 *     "videoAssetIds":[],
			 *     "imageAssetIds":[3,6],
			 *     "audioAssetIds":[],
			 *     "documentAssetIds":null,
			 *     "otherAssetIds":[],
			 *     "cartItemBreachesImageDownloadThreshold":false,
			 *     "cartItemBreachesVideoDownloadThreshold":false
			 * }
			 */
			inspect(data, opts) {
				this.inspection = data;
			},
			/**
			 * Removed assets from cart
			 * @function
			 * @param {number} count Cart asset count
			 * @param {array} assets Assets/asset ids
			 * @param {object} opts Request options
			 */
			removeAssetsFromCart(count, assets, opts) {
				!this.id && this.cartResultParser(count);
			},
		});
	}

	/** @override */
	constructor(models, opts = {}) {
		super(models, opts);

		/**
		 * The cart ID; set to 0 to fetch from the current cart.
		 * @type {number}
		 * @default
		 */
		this.id = 0;

		if (opts.cartId) {
			console.warn(
				'`cartId` is deprecated on the `CartAssets` collections. Use the `id` by version 9.1',
			);
			opts.id = opts.cartId;
		}

		if (opts.id) {
			this.setCartId(opts.id || opts.cartId);
		}

		/**
		 * @memberof Netx.Collections.CartAssets
		 * @property {object} archiveArgs - Arguments to the archive cart assets method
		 * @property {number} archiveArgs.locationId - Archive location id
		 * @default
		 */
		this.archiveArgs = _.extend(
			{
				locationId: 0,
			},
			opts.archiveArgs,
		);

		/**
		 * Target of various batch task methods -- a list of asset IDs, or an empty array to specify the current cart contents.
		 * @memberof Netx.Collections.CartAssets
		 * @type {array}
		 */
		this.batch = [];

		/**
		 * @memberof Netx.Collections.CartAssets
		 * @property {object} expirationArgs - Arguments to the expire cart assets method
		 * @property {number} expirationArgs.date - Expiration date to set (in milliseconds)
		 */
		this.expirationArgs = _.extend(
			{
				date: 0,
			},
			opts.expirationArgs,
		);

		/**
		 * attributes returned by inspectCartContents()
		 * @memberof Netx.Collections.CartAssets
		 * @type {object}
		 */
		this.inspection = _.extend({}, opts.inspection);

		/**
		 * Route of last cart dislplayed
		 * @type {string}
		 */
		this.lastCartRoute = opts.lastCartRoute;

		/**
		 * number of items in the cart, as reported by DAM
		 * @type {number}
		 * @default
		 */
		this.total = opts.total || 0;

		/**
		 * @memberof Netx.Collections.CartAssets
		 * @property {object} unarchiveArgs - Arguments to the unarchive cart assets method
		 * @property {number} unarchiveArgs.unarchiveType - Type of unarchive ( 1 = temporary, 2 = permanently )
		 * @property {string} unarchiveArgs.email         - Email address to notify on unarchive complete
		 * @default
		 */
		this.unarchiveArgs = _.extend(
			{
				type: 1,
				email: '',
			},
			opts.unarchiveArgs,
		);

		// Set selection to cart/cartId
		this.selection.at(0).set({
			elementType: 10, // cart search
			elementValue1: this.id, // cart id (0 for current)
		});
		// Set max items (this already happens in Netx.Collections.Assets - necessary to do here again?)
		this.selection.maxItems = customConfig.CHUNK_SIZE + 1;

		// Hey, listen!
		this.listenTo(this, 'add', this.addAsset)
			// Don't consider a cart sync to be a user selection - I do not see cacheSelection in any code - remove soon? 2015-09-25
			// nk - also this was stopping the super collection sync listener which calls calculatePaging
			// this.stopListening( this, 'sync', this.cacheSelection );
			// always count the cart after a sync
			.listenTo(this, 'sync', this.assetCount)
			.listenTo(
				dispatcher,
				'cart:saved:collect:success cart:saved:add:assets:success',
				(savedCarts) => {
					savedCarts.get(this.id) && this.fetch();
				},
			)
			.listenTo(
				dispatcher,
				[
					'cart:saved:assets:unexpire:succeeded',
					'cart:saved:assets:expire:succeeded',
				].join(' '),
				(savedCart) => {
					this.id === this.model.id && this.fetch();
				},
			);
	}

	/**
	 * @method
	 * @param {number} locationId Archive location id
	 * @param {object} opts Request options
	 */
	archive(locationId, opts) {
		locationId = parseInt(locationId);

		if (isNaN(locationId) || !Netx.app.storageLocations.get(locationId)) {
			return new $.Deferred().reject(
				new Error(i18n.t('i18n.invalidArchiveLocation')),
			);
		}

		this.archiveArgs.locationId = locationId;
		return this.archiveCartAssets(opts);
	}

	/**
	 * Generic handler for cart methods' success
	 * @method
	 * @param {number} count Cart asset count
	 * @todo Check parameter documentation
	 */
	cartResultParser(count) {
		// update our asset count
		this.setAssetCount(count);
		// re-get our list of cart assets from the DAM, now that the search results have been added there.
		return this.fetch();
	}

	/**
	 * @method
	 * @param {string} dateStr Date (yyyy-mm-dd)
	 */
	async expireAssets(dateStr) {
		try {
			const expirationTime = userInputToExpirationTime(dateStr);
			this.expirationArgs.date = expirationTime;
			const uuid = await this.expireCartAssets();
			return [uuid, expirationTime];
		} catch (ex) {
			return new $.Deferred().reject(ex);
		}
	}

	/**
	 * @method
	 * @param {number} page
	 * set page and context for selection, then fetch
	 */
	fetchCurrentCart(page, opts) {
		this.curPage = page ? page : 1;
		this.routeContext = 'cart';
		return this.fetch(opts);
	}

	/**
	 * @method
	 * @param {number} id
	 * @param {number} page
	 * set page and context for savedCart, then fetch
	 */
	fetchSavedCart(id, page, opts) {
		return allSavedCarts.getCart(id).then((savedCart) => {
			this.savedCart = savedCart;
			this.setCartId(id, page);
			return this.fetch(opts);
		});
	}

	/**
	 * Try to find the saved cart model for our ID
	 * @method
	 * @todo this will need to be addressed when we get into paging
	 */
	findCartModel() {
		return allSavedCarts.get(this.id);
	}

	/**
	 * @method
	 * @return {boolen}
	 */
	isSortable() {
		// Cannot sort on cart or custom search types currently
		const customSearchType = this.selection.findWhere({elementType: 9});
		return _.isUndefined(customSearchType);
	}

	/**
	 * We are only allow one poll at a time per instance
	 * @method
	 * @param {array} assetsList An array of asset ids
	 */
	pollRemoveAssets(assetsList) {
		const d = new $.Deferred();

		const checkPolling = () => {
			// Already polling
			if (this._polling) {
				// Wait for poll to complete and try again
				this._polling.promise().then(() => {
					// Otherwise a loop will ensue
					// this._polling will still be defined and so the method will just return the polling promise
					// which immediately is resolved - which would trigger this again - repeat... forever.
					_.defer(() => {
						checkPolling();
					});
				});
			}
			// Ready to poll
			else {
				let assetIds = [];
				this.pollUntil(
					(assets) => {
						if (!_.isUndefined(assets)) {
							assetIds = _.pluck(assets, 'assetId');
							return !_.intersection(assetIds, assetsList).length;
						}
						return false;
					},
					'cart:saved:removing:assets',
					this.fetchAll,
					500,
					{silent: true},
				).then(() => {
					return d.resolve(assetIds);
				});
			}
		};
		checkPolling();

		return d.promise();
	}

	/**
	 * @method
	 * @param {array} assets Array of asset models
	 * @fires modules:cart#cart:saved:removing:assets
	 * @fires modules:cart#cart:removed
	 */
	// TODO: document or fix:  why do we poll after removeAssetsFromCart, but not after addAssetsToCart ?
	// TODO: refactor/unwrap removeAssets, matching how we did it for addAssets.
	removeAssets(assets, opts = {}) {
		const assetsList = _.tidyAssetList(assets);
		const count = this.assetCount();

		return this.removeAssetsFromCart(assetsList, opts).then(() => {
			// Let the world know
			dispatcher.trigger('cart:saved:removing:assets', assetsList, this.id);
			return this.pollRemoveAssets(assetsList).then((assetIds) => {
				// Remove from collection
				this.remove(assetsList);
				dispatcher.trigger('cart:removed', assetsList, this.id);

				this.trigger('sync', this, [], opts).setAssetCount(assetIds.length);

				return assetsList;
			});
		});
	}

	/**
	 * @method
	 * @param {number} count Count
	 */
	setAssetCount(count) {
		// triggers 'counted'
		this.pager.set({itemTotal: parseInt(count)});
		// We kind of painted ourselves into a corner here
		// our actions react to sync events to test enablers
		// we want the cart to reflect the selection immediately
		// upon add/remove from cart - so, a fake sync here.
		this.trigger('sync', this, [], {});

		return this.pager;
	}

	/**
	 * @method
	 * @param {array} assets Array of assets
	 * @fires module:cart#cart:wasSet
	 */
	setAssets(assets) {
		const assetsList = _.tidyAssetList(assets);
		return this.setAssetsInCart(assetsList).then(() => {
			dispatcher.trigger('cart:wasSet', assetsList);
			this.trigger('wasSet', assetsList);
		});
	}

	/**
	 * Set the cart id and page
	 * @param {number} id SavedCart id
	 * @param {number} page
	 */
	setCartId(id, page) {
		const selection = this.selection;
		this.curPage = page;
		this.routeContext = 'cart-assets/' + id;
		id = parseInt(id);
		// triggers 'countUnknown'
		selection.at(0).set({elementValue1: id});

		// reset facets
		if (selection.searchFacets && id != this.id) {
			selection.searchFacets.reset();
		}

		this.id = id;
		this.batch = id === 0 ? [] : [id];
	}

	/**
	 * return an assetSet model defining the set of this cart's assets
	 * @method
	 * @return {module:asset/models/assetSet} AssetSet model
	 */
	toAssetSet() {
		// TODO: this will need to be addressed when we get into paging
		const model = this.findCartModel();
		return new AssetSetModel({
			type: assetSetTypes.savedCart,
			ids: [this.id],
			models: model ? [model] : undefined,
			label: model ? model.get('title') : i18n.t('i18n.cartAssets'),
		});
	}

	/**
	 * Unarchive all assets
	 * @method
	 * @param {number} type  Unarchive type
	 * @param {string} email Email to notify on completion
	 */
	unarchive(type, email) {
		type = parseInt(type);

		if (isNaN(type) || unarchiveTypes[type] == null) {
			return new $.Deferred().reject(
				new Error(i18n.t('i18n.invalidUnarchiveType')),
			);
		}

		_.extend(this.unarchiveArgs, {
			email: email,
			type: type,
		});
		return this.unarchiveCartAssets();
	}

	/**
	 * Unset all asset expiration
	 * @method
	 */
	async unexpireAssets() {
		this.expirationArgs.date = 0;
		const uuid = await this.expireCartAssets();
		return [uuid, 0];
	}

	/**
	 * Instantiate a cartAssets collection with a page of cart assets from a specified cart:
	 * @method
	 * @param {number} id Cart id
	 */
	static Load(id) {
		const instance = new this([], {id: id});
		return instance.fetch().then(() => {
			return this;
		});
	}

	/** @type {string} */
	static get __name__() {
		return 'CartAssets';
	}
}
